// Per-feature display labels for a plan's feature rows (Sept 17 2026 —
// Glenn's ask: three "Active wildfire defense" points on one plan render as
// three identical rows). A strategy that appears once in the plan keeps its
// plain label ("Fire break"); a strategy repeated on the plan numbers each
// occurrence, oldest first ("Fire break 1", "Fire break 2", ...), so the
// numbering stays stable as new features are drawn — a new one always gets
// the next number, never renumbering the ones before it.
//
// Used by the read-only viewers (DefensePlanViewer.web.tsx,
// DefensePlanViewerCard.web.tsx) via DefensePlanFeatureRow's `displayLabel`
// prop. A home feature's address still shows on the meta sub-line as before
// (planFeatureFormat.ts) — this only changes the bold label line above it.

// Duck-typed like planBundles.ts's HasCreatedAt so a plain test object
// works without a real Firestore Timestamp.
interface TimestampLike {
  toMillis(): number;
}

interface LabeledFeature {
  id: string;
  strategy: string;
  createdAt?: TimestampLike | null;
}

/**
 * Returns a label per feature id. `features` can be passed in any order
 * (display order or the plan's stored order) — the numbering is always
 * computed oldest first by `createdAt`, falling back to the given array
 * position when `createdAt` ties or is missing on either side (the same
 * fallback planBundles.ts's orderFeaturesNewestFirst uses, just counting
 * the other direction).
 *
 * `strategyLabel` looks up a strategy's plain label (normally
 * `DEFENSE_STRATEGIES[strategy].label`) — passed in rather than imported so
 * this stays a pure function with no dependency on the strategy palette
 * module.
 */
export function computeFeatureLabels<F extends LabeledFeature>(
  features: F[],
  strategyLabel: (strategy: F['strategy']) => string
): Record<string, string> {
  const oldestFirst = features
    .map((feature, index) => ({ feature, index }))
    .sort((a, b) => {
      const aMillis = a.feature.createdAt?.toMillis() ?? null;
      const bMillis = b.feature.createdAt?.toMillis() ?? null;
      if (aMillis !== null && bMillis !== null && aMillis !== bMillis) {
        return aMillis - bMillis;
      }
      return a.index - b.index;
    })
    .map(({ feature }) => feature);

  const totalByStrategy = new Map<string, number>();
  for (const feature of features) {
    totalByStrategy.set(feature.strategy, (totalByStrategy.get(feature.strategy) ?? 0) + 1);
  }

  const labels: Record<string, string> = {};
  const seenByStrategy = new Map<string, number>();
  for (const feature of oldestFirst) {
    const label = strategyLabel(feature.strategy);
    if ((totalByStrategy.get(feature.strategy) ?? 0) <= 1) {
      labels[feature.id] = label;
      continue;
    }
    const next = (seenByStrategy.get(feature.strategy) ?? 0) + 1;
    seenByStrategy.set(feature.strategy, next);
    labels[feature.id] = `${label} ${next}`;
  }
  return labels;
}
